export type TransformKind =
  | 'Copy' | 'Concat' | 'SplitFirst' | 'SplitRest' | 'Currency' | 'Number' | 'Phone' | 'Email' | 'Bool'
  | 'Date' | 'CountryName' | 'CountryCode' | 'ValueMap' | 'LineAt' | 'ExcelFloatToText' | 'GenerateCode' | 'Constant' | 'AiClean';

export const TRANSFORM_KINDS: TransformKind[] = [
  'Copy', 'Concat', 'SplitFirst', 'SplitRest', 'Currency', 'Number', 'Phone', 'Email', 'Bool',
  'Date', 'CountryName', 'CountryCode', 'ValueMap', 'LineAt', 'ExcelFloatToText', 'GenerateCode', 'Constant', 'AiClean',
];

export const TRANSFORM_LABELS: Record<TransformKind, string> = {
  Copy: 'Copy unchanged',
  Concat: 'Join columns',
  SplitFirst: 'First word',
  SplitRest: 'Everything after the first word',
  Currency: 'Money amount',
  Number: 'Number',
  Phone: 'Phone number',
  Email: 'Email address',
  Bool: 'Yes / no',
  Date: 'Date',
  CountryName: 'Country name',
  CountryCode: 'Country code',
  ValueMap: 'Map values',
  LineAt: 'Line from multi-line cell',
  ExcelFloatToText: 'Number stored as text',
  GenerateCode: 'Generate a code',
  Constant: 'Fixed value',
  AiClean: 'Clean up with AI',
};

export const TRANSFORMS_WITH_ARGUMENT: Partial<Record<TransformKind, string>> = {
  Concat: 'Separator',
  SplitFirst: 'Separator',
  SplitRest: 'Separator',
  LineAt: 'Line number',
  Constant: 'Value',
  GenerateCode: 'Prefix',
  Date: 'dmy or mdy',
  AiClean: 'What to correct, and the format required',
};

export type RowFilterOperator = 'Equals' | 'NotEquals' | 'IsEmpty' | 'IsNotEmpty' | 'Contains';

export interface FieldMapping {
  Target: string;
  Sources: number[];
  Transform: TransformKind;
  Argument?: string | null;
  Confidence: number;
  Reason?: string | null;
}

export interface IgnoredColumn {
  ColumnIndex: number;
  Reason?: string | null;
}

export interface ValueMapEntry {
  From: string;
  To: string;
}

export interface ValueMap {
  Target: string;
  Entries: ValueMapEntry[];
}

export interface RowFilter {
  ColumnIndex: number;
  Operator: RowFilterOperator;
  Value?: string | null;
  KeepMatching: boolean;
}

export interface ImportRecipe {
  SheetIndex: number;
  HeaderRowIndex: number;
  DataStartRowIndex: number;
  SourceLabel?: string | null;
  Fields: FieldMapping[];
  Ignored: IgnoredColumn[];
  ValueMaps: ValueMap[];
  RowFilter?: RowFilter | null;
  Warnings: string[];
}

export interface ColumnProfile {
  Index: number;
  Header: string;
  NonEmptyCount: number;
  RowCount: number;
  DistinctCount: number;
  DominantPattern: string;
  DominantRatio: number;
  MaxLength: number;
  HasNumericCells: boolean;
  Samples: string[];
  FillRatio: number;
}

export type RecipeProblemCode = 'ContactNameUnmapped' | 'CustomFieldMapping' | 'CountryFromNonCountryColumn' | 'MixedContacts' | 'RetiredTarget' | 'TypeLooksLikeCategory';

export interface RecipeProblem {
  Severity: number;
  Target?: string | null;
  Message: string;
  Code?: RecipeProblemCode | string | null;
  ColumnIndex?: number | null;
}

export const PROBLEM_ERROR = 0;
export const PROBLEM_WARNING = 1;

export interface IssueCount {
  Target: string;
  Message: string;
  Count: number;
}

export interface FieldFill {
  Target: string;
  Filled: number;
  Total: number;
}

export interface PreviewSummary {
  TotalRows: number;
  RowsWithIssues: number;
  HeldBack?: number;
  SkippedEmptyRows: number;
  SkippedByFilter: number;
  IssuesByField: IssueCount[];
  BlockingIssues?: IssueCount[];
  FieldFill: FieldFill[];
  ErrorExplanation?: string | null;
  ImportFailureExplanation?: string | null;
  LeadingZerosRestored?: number | null;
  LeadingZeroUncertain?: number | null;
  LeadingZerosUncertain?: number | null;
  ImportOutcome?: string | null;
}

export interface TargetFieldInfo {
  Name: string;
  Description: string;
  Required: boolean;
  Kind: string;
  AllowedValues?: string[] | null;
}

export interface ImportDraft {
  ID: string;
  ImportType: number;
  Stage: number;
  Mode: number;
  ModeReason?: string | null;
  ProgressDone: number;
  ProgressTotal: number;
  ProgressRecords: number;
  SourceFileName: string;
  SourceFileSize: number;
  UserInstruction?: string | null;
  RecipeSource: number;
  Recipe?: ImportRecipe | null;
  Profiles: ColumnProfile[];
  Problems: RecipeProblem[];
  Summary?: PreviewSummary | null;
  TargetFields: TargetFieldInfo[];
  HeaderFingerprint?: string | null;
  SourceLabel?: string | null;
  SavedImportRecipeID?: string | null;
  ImportID?: string | null;
  ImportFailureExplanation?: string | null;
  Error?: string | null;
  ProviderKey?: string | null;
  Model?: string | null;
  InputTokens: number;
  OutputTokens: number;
  InferenceCount: number;
  RowCount: number;
  IssueRowCount: number;
  DurationMs: number;
  CreatedDate: string;
  ModifiedDate: string;
  CreatedBy?: string | null;
}

export interface RowWarning {
  Target: string;
  Message: string;
  BlocksQueue?: boolean;
}

export interface AppliedRow {
  SourceRowIndex: number;
  SourceText?: string | null;
  Values: Record<string, string | null>;
  Warnings: RowWarning[];
}

export interface PreviewPage {
  PageIndex: number;
  PageSize: number;
  TotalRows: number;
  Rows: AppliedRow[];
}

export interface ImportDraftPreview {
  DraftID: string;
  Summary: PreviewSummary;
  Page: PreviewPage;
  Columns: string[];
}

export interface SourceSampleRow {
  RowIndex: number;
  Cells: string[];
}

export interface SourceSample {
  SheetIndex: number;
  SheetName: string;
  HeaderRowIndex: number;
  TotalRows: number;
  ColumnCount: number;
  Headers: string[];
  Rows: SourceSampleRow[];
}

export interface ImportDraftCommitResult {
  DraftID: string;
  ImportID: string;
  RowCount: number;
}

export interface ApiError {
  status: number;
  message: string;
  serverMessage?: string | null;
  Message?: string;
}
